import React, { useEffect, useState, useRef } from 'react';
import { BlocManagementVouchers, VoucherViewState } from '../../../bloc/BlocManagementVouchers';
import { VoucherWithCodes, CreateVoucherRequest, UpdateVoucherRequest, VoucherCode } from '../../../../types/voucher.types';
import { VoucherManagementService } from '../../../../service/supabase/VoucherManagementService';
import StreamBuilder from '../../../components/common/StreamBuilder';
import Button from '../../../components/common/Button';
import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdmin } from '../../../../base/AdminContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './VoucherCodeRandomPage.css';
import VoucherCodeRandomForm from './components/VoucherCodeRandomForm';


const VoucherCodeRandomPage: React.FC = () => {
    const { checkPermission } = useAdmin();
    const [bloc] = useState(() => new BlocManagementVouchers());
    const [searchTerm, setSearchTerm] = useState('');
    const [showPageHelp, setShowPageHelp] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedVehicleType, setSelectedVehicleType] = useState<string>('');
    const [lazyLoading, setLazyLoading] = useState<boolean>(false);

    // Voucher codes dialog states
    const [showCodesDialog, setShowCodesDialog] = useState<boolean>(false);
    const [selectedVoucherForCodes, setSelectedVoucherForCodes] = useState<VoucherWithCodes | null>(null);
    const [voucherCodes, setVoucherCodes] = useState<VoucherCode[]>([]);
    const [loadingCodes, setLoadingCodes] = useState<boolean>(false);
    const [codesSearchTerm, setCodesSearchTerm] = useState('');
    const [codesStatusFilter, setCodesStatusFilter] = useState<string>('');

    const toast = useRef<Toast>(null);

    // Check permissions for voucher management
    const canAddVoucher = checkPermission('voucher_management', 'vouchers_random_code', 'add');
    const canEditVoucher = checkPermission('voucher_management', 'vouchers_random_code', 'edit');
    const canDeleteVoucher = checkPermission('voucher_management', 'vouchers_random_code', 'delete');

    useEffect(() => {
        // Set filter for random code vouchers only
        bloc.setFilters({ voucher_type: 'random_code' as any });
        bloc.initialize();
        return () => bloc.dispose?.();
    }, [bloc]);

    // Search handler with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            bloc.setFilters({ search: searchTerm, voucher_type: 'random_code' as any });
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, bloc]);

    // Filter handlers
    const handleStatusFilter = async (status: string) => {
        setSelectedStatus(status);
        bloc.setFilters({
            ...bloc.info.filters,
            status: status ? status as 'active' | 'expired' | 'upcoming' : undefined,
            voucher_type: 'random_code' as any
        });
    };

    const handleVehicleTypeFilter = async (vehicleType: string) => {
        setSelectedVehicleType(vehicleType);
        bloc.setFilters({
            ...bloc.info.filters,
            voucher_type_vehicle: vehicleType ? vehicleType as 'car' | 'motorbike' | 'both' : undefined,
            voucher_type: 'random_code' as any
        });
    };

    // Clear filters
    const clearFilters = async () => {
        setSearchTerm('');
        setSelectedStatus('');
        setSelectedVehicleType('');
        bloc.setFilters({ voucher_type: 'random_code' as any });
    };

    // Page change handler for DataTable's onPage event
    const onPageChange = async (event: any) => {
        setLazyLoading(true);
        // DataTable's page is 0-based, but our API expects 1-based
        await bloc.goToPage(event.page + 1);
        setLazyLoading(false);
    };

    // Refresh handler
    const handleRefresh = async () => {
        await bloc.refreshVouchers();
    };

    const handleDeleteVoucher = (voucher: VoucherWithCodes) => {
        if (!canDeleteVoucher) {
            console.warn('Không có quyền xóa voucher');
            return;
        }

        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa voucher mã ngẫu nhiên này? Tất cả mã code liên quan sẽ bị xóa. Hành động này không thể hoàn tác.`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Xóa',
            rejectLabel: 'Hủy',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await bloc.deleteVoucher(voucher.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Xóa voucher mã ngẫu nhiên thành công',
                        life: 3000
                    });
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: 'Không thể xóa voucher mã ngẫu nhiên',
                        life: 5000
                    });
                }
            }
        });
    };

    const handleCloneVoucher = (voucher: VoucherWithCodes) => {
        if (!canAddVoucher) {
            console.warn('Không có quyền sao chép voucher');
            return;
        }

        confirmDialog({
            message: `Bạn có muốn sao chép voucher mã ngẫu nhiên này?`,
            header: 'Xác nhận sao chép',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Sao chép',
            rejectLabel: 'Hủy',
            accept: async () => {
                try {
                    await bloc.cloneVoucher(voucher.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Sao chép voucher mã ngẫu nhiên thành công',
                        life: 3000
                    });
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: 'Không thể sao chép voucher mã ngẫu nhiên',
                        life: 5000
                    });
                }
            }
        });
    };

    const handleViewCodes = async (voucher: VoucherWithCodes) => {
        setSelectedVoucherForCodes(voucher);
        setShowCodesDialog(true);
        setLoadingCodes(true);
        setVoucherCodes([]); // Reset codes
        setCodesSearchTerm(''); // Reset search
        setCodesStatusFilter(''); // Reset filter

        await loadVoucherCodes(voucher.id);
    };

    const loadVoucherCodes = async (voucherId: string, search?: string, statusFilter?: string) => {
        setLoadingCodes(true);

        try {
            const filters: any = {};

            if (search) {
                filters.search = search;
            }

            if (statusFilter !== '' && statusFilter !== undefined) {
                filters.is_redeemed = statusFilter === 'redeemed';
            }

            // Gọi API để lấy danh sách voucher codes từ server
            const response = await VoucherManagementService.getVoucherCodes(voucherId, 1, 100, filters);
            setVoucherCodes(response.data);

            console.log(`Loaded ${response.data.length} voucher codes for voucher ${voucherId}`, filters);
        } catch (error) {
            console.error('Error loading voucher codes:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Không thể tải danh sách mã code',
                life: 5000
            });
        } finally {
            setLoadingCodes(false);
        }
    };

    const handleCodesSearch = (searchValue: string) => {
        setCodesSearchTerm(searchValue);
        if (selectedVoucherForCodes) {
            loadVoucherCodes(selectedVoucherForCodes.id, searchValue, codesStatusFilter);
        }
    };

    const handleCodesStatusFilter = (status: string) => {
        setCodesStatusFilter(status);
        if (selectedVoucherForCodes) {
            loadVoucherCodes(selectedVoucherForCodes.id, codesSearchTerm, status);
        }
    };

    // Form submission handlers
    const handleFormSubmit = async (data: CreateVoucherRequest | UpdateVoucherRequest) => {
        try {
            if ('id' in data && data.id) {
                // Update existing voucher
                await bloc.updateVoucher(data as UpdateVoucherRequest);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Cập nhật voucher mã ngẫu nhiên thành công',
                    life: 3000
                });
            } else {
                // Create new voucher
                await bloc.createVoucher(data as CreateVoucherRequest);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Tạo voucher mã ngẫu nhiên thành công',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Có lỗi xảy ra khi xử lý voucher',
                life: 5000
            });
        }
    };

    const getVoucherStatus = (voucher: VoucherWithCodes): 'active' | 'expired' | 'upcoming' => {
        const now = new Date();
        const startDate = new Date(voucher.start_date);
        const endDate = new Date(voucher.end_date);

        if (now < startDate) return 'upcoming';
        if (now > endDate) return 'expired';
        return 'active';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND'
        }).format(amount);
    };

    // Column templates for DataTable
    const nameBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="voucher-name-cell">
            <div className="voucher-main-info">
                <span className="font-medium text-gray-900">Mã ngẫu nhiên</span>
                <Tag
                    value="Multi-code"
                    style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                    className="ml-2"
                />
            </div>
            <div className="text-sm text-purple-600 font-medium">
                {rowData.codes_count || 0} mã code
            </div>
        </div>
    );

    const discountBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="discount-cell">
            {rowData.discount_value ? (
                <span className="font-medium">{formatCurrency(rowData.discount_value)}</span>
            ) : (
                <span className="font-medium">{rowData.discount_percentage}%</span>
            )}
            {rowData.minimum_amount && (
                <div className="text-sm text-gray-500">
                    Tối thiểu: {formatCurrency(rowData.minimum_amount)}
                </div>
            )}
        </div>
    );

    const statusBodyTemplate = (rowData: VoucherWithCodes) => {
        const status = getVoucherStatus(rowData);
        const statusConfig = {
            active: { label: 'Đang hoạt động', severity: 'success' as const },
            expired: { label: 'Đã hết hạn', severity: 'danger' as const },
            upcoming: { label: 'Sắp diễn ra', severity: 'warning' as const }
        };

        return <Tag value={statusConfig[status].label} severity={statusConfig[status].severity} />;
    };

    const usageBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="usage-cell">
            <div className="font-medium">
                {rowData.total_redemptions}/{rowData.total_redemption_limit || '∞'}
            </div>
            <div className="text-sm text-gray-500">đã sử dụng</div>
        </div>
    );

    const vehicleBodyTemplate = (rowData: VoucherWithCodes) => {
        if (!rowData.voucher_type_vehicle || rowData.voucher_type_vehicle === 'both') {
            return <Tag value="Tất cả" severity="info" />;
        }
        return (
            <Tag
                value={rowData.voucher_type_vehicle === 'car' ? 'Ô tô' : 'Xe máy'}
                severity="info"
            />
        );
    };

    const dateTemplate = (dateString: string) => (
        <span className="text-sm text-gray-600">
            {formatDate(dateString)}
        </span>
    );

    const actionBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="flex gap-2">
            <Button
                variant="primary"
                size="small"
                onClick={() => handleViewCodes(rowData)}
            >
                <i className="pi pi-list"></i>
            </Button>
            {canEditVoucher && (
                <Button
                    variant="secondary"
                    size="small"
                    onClick={() => bloc.openEditForm(rowData)}
                >
                    <i className="pi pi-pencil"></i>
                </Button>
            )}
            <Button
                variant="secondary"
                size="small"
                onClick={() => handleCloneVoucher(rowData)}
            >
                <i className="pi pi-copy"></i>
            </Button>
            {canDeleteVoucher && (
                <Button
                    variant="danger"
                    size="small"
                    onClick={() => handleDeleteVoucher(rowData)}
                >
                    <i className="pi pi-trash"></i>
                </Button>
            )}
        </div>
    );

    // Filter options
    const statusOptions = [
        { label: 'Tất cả trạng thái', value: '' },
        { label: 'Đang hoạt động', value: 'active' },
        { label: 'Đã hết hạn', value: 'expired' },
        { label: 'Sắp diễn ra', value: 'upcoming' }
    ];

    const vehicleTypeOptions = [
        { label: 'Tất cả phương tiện', value: '' },
        { label: 'Ô tô', value: 'car' },
        { label: 'Xe máy', value: 'motorbike' },
        { label: 'Tất cả', value: 'both' }
    ];

    return (
        <AdminProtectedRoute
            requiredModule="voucher_management"
            requiredSubModule="vouchers_random_code"
            requiredPermission="view"
        >
            <AdminLayout>
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    {/* Page Header */}
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                                    Voucher Mã Ngẫu Nhiên
                                </h1>
                                <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                                    Quản lý voucher có nhiều mã code ngẫu nhiên - thường dùng cho chiến dịch phát hành nhiều mã
                                </p>
                            </div>
                            <Button
                                variant="secondary"
                                size="small"
                                onClick={() => setShowPageHelp(true)}
                            >
                                <i className="pi pi-question-circle" style={{ marginRight: '4px' }}></i>
                                Hướng dẫn
                            </Button>
                        </div>
                    </div>

                    {/* Table Container */}
                    <div style={{ flex: 1, minHeight: 0, background: 'white' }}>
                        <StreamBuilder
                            stream={bloc.getStream('voucherViewState')}
                            initialData={bloc.info}
                            builder={(snapshot) => {
                                const state: VoucherViewState = snapshot.data || bloc.info;

                                if (state.error) {
                                    return (
                                        <div style={{ padding: '24px' }}>
                                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '16px' }}>
                                                <div style={{ display: 'flex' }}>
                                                    <i className="pi pi-exclamation-triangle" style={{ color: '#f87171', marginRight: '8px' }}></i>
                                                    <div style={{ color: '#991b1b' }}>
                                                        <h3 style={{ fontSize: '0.875rem', fontWeight: '500' }}>Có lỗi xảy ra</h3>
                                                        <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>{state.error}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // Header template with filters
                                const headerTemplate = (
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '16px',
                                        alignItems: 'center',
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f9fafb',
                                        width: '100%'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 250px', minWidth: '250px' }}>
                                            <i className="pi pi-search" style={{ color: '#6b7280', fontSize: '0.875rem' }}></i>
                                            <input
                                                type="text"
                                                placeholder="Tìm kiếm voucher mã ngẫu nhiên..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{
                                                    flex: 1,
                                                    fontSize: '0.875rem',
                                                    padding: '8px 12px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '6px',
                                                    minWidth: '200px'
                                                }}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={clearFilters}
                                            >
                                                <i className="pi pi-filter-slash"></i>
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={handleRefresh}
                                            >
                                                <i className="pi pi-refresh"></i>
                                            </Button>
                                            {canAddVoucher && (
                                                <Button
                                                    variant="primary"
                                                    size="small"
                                                    onClick={() => bloc.openCreateForm()}
                                                >
                                                    <i className="pi pi-plus" style={{ marginRight: '4px' }}></i>
                                                    Thêm voucher
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );

                                return (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {/* Table */}
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <DataTable
                                                scrollHeight='calc(100vh - 200px)'
                                                value={state.vouchers}
                                                loading={state.loading || lazyLoading}
                                                loadingIcon="pi pi-spinner pi-spin"
                                                className="vouchers-management-table"
                                                emptyMessage={searchTerm ?
                                                    'Không tìm thấy voucher mã ngẫu nhiên nào phù hợp' :
                                                    'Chưa có voucher mã ngẫu nhiên nào'
                                                }
                                                responsiveLayout="scroll"
                                                scrollable

                                                size="small"
                                                stripedRows
                                                header={headerTemplate}
                                                showGridlines
                                                paginator={state.pagination.total > 0}
                                                rows={state.pagination.limit}
                                                totalRecords={state.pagination.total}
                                                first={(state.pagination.page - 1) * state.pagination.limit}
                                                onPage={onPageChange}
                                                rowsPerPageOptions={[10, 20, 50]}
                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                                currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} voucher"
                                            >
                                                <Column
                                                    field="type"
                                                    header="Loại voucher"
                                                    body={nameBodyTemplate}
                                                    style={{ minWidth: '200px' }}
                                                    frozen
                                                />
                                                <Column
                                                    field="discount"
                                                    header="Giảm giá"
                                                    body={discountBodyTemplate}
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    field="status"
                                                    header="Trạng thái"
                                                    body={statusBodyTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedStatus}
                                                            options={statusOptions}
                                                            onChange={(e) => handleStatusFilter(e.value)}
                                                            placeholder="Tất cả trạng thái"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="usage"
                                                    header="Sử dụng"
                                                    body={usageBodyTemplate}
                                                    style={{ minWidth: '100px' }}
                                                />
                                                <Column
                                                    field="vehicle_type"
                                                    header="Phương tiện"
                                                    body={vehicleBodyTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedVehicleType}
                                                            options={vehicleTypeOptions}
                                                            onChange={(e) => handleVehicleTypeFilter(e.value)}
                                                            placeholder="Tất cả phương tiện"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="start_date"
                                                    header="Bắt đầu"
                                                    body={(rowData) => dateTemplate(rowData.start_date)}
                                                    sortable
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    field="end_date"
                                                    header="Kết thúc"
                                                    body={(rowData) => dateTemplate(rowData.end_date)}
                                                    sortable
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    header="Thao tác"
                                                    body={actionBodyTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    frozen
                                                    alignFrozen="right"
                                                />
                                            </DataTable>
                                        </div>

                                        {/* Show message when no data and not loading */}
                                        {!state.loading && state.vouchers.length === 0 && (
                                            <div style={{ flexShrink: 0, padding: '32px', textAlign: 'center' }}>
                                                <div style={{ color: '#6b7280' }}>
                                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '12px', display: 'block' }}></i>
                                                    <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>Chưa có voucher mã ngẫu nhiên</p>
                                                    <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>Hệ thống chưa có voucher mã ngẫu nhiên nào được tạo.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>

                {/* Voucher Form - Inside StreamBuilder to react to state changes */}
                <StreamBuilder
                    stream={bloc.getStream('voucherViewState')}
                    initialData={bloc.info}
                    builder={(snapshot) => {
                        const state: VoucherViewState = snapshot.data || bloc.info;
                        return (
                            <VoucherCodeRandomForm
                                visible={state.isFormOpen}
                                voucher={state.selectedVoucher}
                                onHide={() => bloc.closeForm()}
                                onSubmit={handleFormSubmit}
                                loading={state.loading}
                                isEdit={state.formType === 'edit'}
                            />
                        );
                    }}
                />

                {/* Voucher Codes List Dialog */}
                <Dialog
                    header={`Danh sách mã code - ${selectedVoucherForCodes?.title || 'Voucher'}`}
                    visible={showCodesDialog}
                    onHide={() => {
                        setShowCodesDialog(false);
                        setSelectedVoucherForCodes(null);
                        setVoucherCodes([]);
                        setCodesSearchTerm('');
                        setCodesStatusFilter('');
                    }}
                    modal
                    className="voucher-codes-dialog"
                    style={{ width: '900px' }}
                    maximizable
                >
                    <div className="voucher-codes-content">
                        {selectedVoucherForCodes && (
                            <div className="voucher-info-summary" style={{
                                marginBottom: '1.5rem',
                                padding: '1rem',
                                background: '#f8fafc',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0'
                            }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                    <div>
                                        <strong>Tiêu đề:</strong> {selectedVoucherForCodes.title}
                                    </div>
                                    <div>
                                        <strong>Loại giảm giá:</strong> {
                                            selectedVoucherForCodes.discount_value
                                                ? `${formatCurrency(selectedVoucherForCodes.discount_value)}`
                                                : `${selectedVoucherForCodes.discount_percentage}%`
                                        }
                                    </div>
                                    <div>
                                        <strong>Tổng số mã:</strong> {selectedVoucherForCodes.codes_count || 0}
                                    </div>
                                    <div>
                                        <strong>Đã sử dụng:</strong> {voucherCodes.filter(code => code.is_redeemed).length}/{voucherCodes.length}
                                    </div>
                                    <div>
                                        <strong>Tỷ lệ sử dụng:</strong> {
                                            voucherCodes.length > 0
                                                ? `${Math.round((voucherCodes.filter(code => code.is_redeemed).length / voucherCodes.length) * 100)}%`
                                                : '0%'
                                        }
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Codes table header with search and filter */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px',
                            alignItems: 'center',
                            marginBottom: '1rem',
                            padding: '12px',
                            background: '#f9fafb',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 200px', minWidth: '200px' }}>
                                <i className="pi pi-search" style={{ color: '#6b7280', fontSize: '0.875rem' }}></i>
                                <input
                                    type="text"
                                    placeholder="Tìm kiếm mã code..."
                                    value={codesSearchTerm}
                                    onChange={(e) => handleCodesSearch(e.target.value)}
                                    style={{
                                        flex: 1,
                                        fontSize: '0.875rem',
                                        padding: '6px 10px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        minWidth: '150px'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ fontSize: '0.875rem', color: '#374151' }}>Trạng thái:</label>
                                <Dropdown
                                    value={codesStatusFilter}
                                    options={[
                                        { label: 'Tất cả', value: '' },
                                        { label: 'Chưa sử dụng', value: 'not_redeemed' },
                                        { label: 'Đã sử dụng', value: 'redeemed' }
                                    ]}
                                    onChange={(e) => handleCodesStatusFilter(e.value)}
                                    style={{ minWidth: '120px', fontSize: '0.75rem' }}
                                />
                            </div>

                            <Button
                                variant="secondary"
                                size="small"
                                onClick={() => selectedVoucherForCodes && loadVoucherCodes(selectedVoucherForCodes.id)}
                                disabled={loadingCodes}
                            >
                                <i className="pi pi-refresh"></i>
                            </Button>
                        </div>

                        <DataTable
                            scrollHeight='calc(100vh - 200px)'
                            value={voucherCodes}
                            loading={loadingCodes}
                            className="voucher-codes-table"
                            emptyMessage="Không có mã code nào"
                            responsiveLayout="scroll"
                            scrollable

                            size="small"
                            stripedRows
                            showGridlines
                            paginator
                            rows={20}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} mã code"
                        >
                            <Column
                                field="code"
                                header="Mã Code"
                                style={{ minWidth: '150px' }}
                                body={(rowData) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{
                                            fontFamily: 'monospace',
                                            fontWeight: 'bold',
                                            color: rowData.is_redeemed ? '#6b7280' : '#1f2937'
                                        }}>
                                            {rowData.code}
                                        </span>
                                        {rowData.is_redeemed && (
                                            <Tag value="Đã dùng" severity="danger" />
                                        )}
                                    </div>
                                )}
                            />
                            <Column
                                field="is_redeemed"
                                header="Trạng thái"
                                style={{ minWidth: '120px' }}
                                body={(rowData) => (
                                    <Tag
                                        value={rowData.is_redeemed ? 'Đã sử dụng' : 'Chưa sử dụng'}
                                        severity={rowData.is_redeemed ? 'danger' : 'success'}
                                    />
                                )}
                            />
                            <Column
                                field="redeemed_at"
                                header="Thời gian sử dụng"
                                style={{ minWidth: '150px' }}
                                body={(rowData) =>
                                    rowData.redeemed_at ? (
                                        <span className="text-sm text-gray-600">
                                            {formatDate(rowData.redeemed_at)}
                                        </span>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )
                                }
                            />                            <Column
                                field="redeemed_by"
                                header="ID Người sử dụng"
                                style={{ minWidth: '150px' }}
                                body={(rowData) =>
                                    rowData.redeemed_by ? (
                                        <span className="text-sm text-gray-600 font-mono">
                                            {rowData.redeemed_by}
                                        </span>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )
                                }
                            />
                            <Column
                                field="created_at"
                                header="Ngày tạo"
                                style={{ minWidth: '150px' }}
                                body={(rowData) => (
                                    <span className="text-sm text-gray-600">
                                        {formatDate(rowData.created_at)}
                                    </span>
                                )}
                            />
                        </DataTable>
                    </div>
                </Dialog>

                {/* Page Help Dialog */}
                <Dialog
                    header="Hướng dẫn Quản lý Voucher Mã Ngẫu Nhiên"
                    visible={showPageHelp}
                    onHide={() => setShowPageHelp(false)}
                    modal
                    className="page-help-dialog"
                    style={{ width: '800px' }}
                >
                    <div className="page-help-content">
                        <div className="help-section">
                            <h3><i className="pi pi-info-circle"></i> Voucher Mã Ngẫu Nhiên là gì?</h3>
                            <p>Voucher mã ngẫu nhiên là loại voucher có nhiều mã code ngẫu nhiên (ví dụ: "Sự kiện" với 10 mã như EVENT2025K1), thường dùng cho chiến dịch phát hành nhiều mã để người dùng nhận.</p>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-cog"></i> Các trường bắt buộc</h3>
                            <ul>
                                <li><strong>multi_code = TRUE:</strong> Xác định voucher có nhiều mã code</li>
                                <li><strong>is_public = FALSE:</strong> Thường là riêng tư vì mã được phát hành có chọn lọc</li>
                                <li><strong>discount_value hoặc discount_percentage:</strong> Xác định giá trị giảm giá (chỉ một trong hai)</li>
                                <li><strong>start_date và end_date:</strong> Thời gian hiệu lực của voucher</li>
                                <li><strong>max_usage:</strong> Số lần tối đa mỗi mã code được sử dụng</li>
                                <li><strong>total_redemption_limit:</strong> Giới hạn tổng số lần tất cả mã code được đổi (bắt buộc)</li>
                                <li><strong>number_of_codes:</strong> Số lượng mã code cần tạo</li>
                                <li><strong>code_prefix:</strong> Tiền tố cho các mã code (ví dụ: EVENT2025)</li>
                            </ul>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-list"></i> Các trường tùy chọn</h3>
                            <ul>
                                <li><strong>minimum_amount:</strong> Giá trị đơn hàng tối thiểu để áp dụng</li>
                                <li><strong>voucher_type_vehicle:</strong> Loại xe áp dụng (car, motorbike, both)</li>
                                <li><strong>user_redemption_limit:</strong> Số lần đổi tối đa của mỗi người dùng</li>
                            </ul>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-times-circle"></i> Không cần thiết</h3>
                            <ul>
                                <li><strong>code:</strong> Không cần mã code cố định vì mã được quản lý trong voucher_codes</li>
                            </ul>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-sync"></i> Cách thức hoạt động</h3>
                            <ul>
                                <li>Admin tạo voucher mã ngẫu nhiên với số lượng mã code cụ thể</li>
                                <li>Hệ thống tự động tạo các mã code ngẫu nhiên theo pattern định sẵn</li>
                                <li>Các mã code được lưu trong bảng voucher_codes</li>
                                <li>Người dùng nhận mã code thông qua chiến dịch marketing</li>
                                <li>Mỗi mã code có thể được sử dụng theo giới hạn max_usage</li>
                            </ul>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-lightbulb"></i> Lưu ý quan trọng</h3>
                            <ul>
                                <li>Mỗi mã code là duy nhất và được tạo tự động</li>
                                <li>total_redemption_limit phải được thiết lập để kiểm soát tổng số lần sử dụng</li>
                                <li>Thường dùng cho các chiến dịch khuyến mãi có thời hạn</li>
                                <li>Voucher mã ngẫu nhiên không yêu cầu điểm để đổi (miễn phí)</li>
                                <li>Chỉ có thể chọn một trong hai: giảm theo số tiền hoặc phần trăm</li>
                            </ul>
                        </div>
                    </div>
                </Dialog>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};

export default VoucherCodeRandomPage;
